/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/InternalQueryServiceAPI',
	'../../../widgets/livewidget/query/QueryService',
	'./api/InternalQueryServiceErrorAPI',
	'./InternalQueryServiceError',
	'../../../widgets/livewidget/nls/StringResources'
], (APIFactory, InternalQueryServiceAPI, QueryService, InternalQueryServiceErrorAPI, InternalQueryServiceError, StringResources) => {
	class InternalQueryService {
		static PROPID_QUERY_OPTIMIZATION = 'queryOptimization'
		static PROPID_DATA_CACHE = 'dataCache'

		constructor({ features, spec }) {
			this.dashboard = features['API'];
			this.internal = features['internal'];
			this.properties = features['Properties'];
			this.queryService = new QueryService({
				dashboardApi: this.dashboard
			});
			this.internalQueryServiceError = new InternalQueryServiceError(this.dashboard);
			this.featureChecker = this.dashboard && this.dashboard.getGlassCoreSvc('.FeatureChecker');
			this.affinityService = features['AffinityService'];
			this._optimizerFeature = features['QueryOptimizerService'];	//cache this feature to use if enabled.

			this.featureModel = spec || {};
		}

		initialize() {
			this.properties && this.properties.registerProvider(this);
		}

		getAPI(type) {
			if (type === 'error') {
				if(!this.errorAPI) {
					this.errorAPI = APIFactory.createAPI(this.internalQueryServiceError, [InternalQueryServiceErrorAPI]);
				}
				return this.errorAPI;
			}
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [InternalQueryServiceAPI]);
			}
			return this.api;
		}

		executeQuery(sourceId, querySpec, senderId, requestOptions = {}) {
			let runner = this.queryService;
			if( this._isQueryOptimizationEnabled() ) {
				this.queryOptimizerService = this._optimizerFeature;
				this.queryOptimizerService && this.queryOptimizerService.setup(this.queryService);
				runner = this.queryOptimizerService;
			}

			return this.affinityService.getAffinityContext().then(affinityContext => {
				if (affinityContext) {
					requestOptions.getAdditionalHeaders = this._getAdditionalHeaders.bind(this, affinityContext);
				}
				return runner.runQuery({
					sourceIdOrModule: sourceId,
					querySpec: querySpec,
					requestOptions: requestOptions,
					sender: senderId
				}).catch(jqXHR => {
					if (affinityContext && this._checkForAffinityError(jqXHR)){
						return this._resetAffinityAndResend(runner, sourceId, querySpec, senderId, requestOptions);
					}
					throw jqXHR;
				});
			});
		}

		_checkForAffinityError(jqXHR){
			return !!(jqXHR.data && jqXHR.data.errors && jqXHR.data.errors.length === 1 && jqXHR.data.errors[0].code === 'DSS-RST-0023');
		}

		_resetAffinityAndResend(runner, sourceId, querySpec, senderId, requestOptions){
			this.affinityService.resetAffinity();
			return this.affinityService.getAffinityContext().then(affinityContext => {
				if (affinityContext) {
					requestOptions.getAdditionalHeaders = this._getAdditionalHeaders.bind(this, affinityContext);
				}
				return runner.runQuery({
					sourceIdOrModule: sourceId,
					querySpec: querySpec,
					requestOptions: requestOptions,
					sender: senderId
				});
			});
		}

		_getAdditionalHeaders(affinityContext) {
			let requestContext = {
				// Temporarily add the requestID here. When implement cancel, each query class should own its requestID
				// and update whenever generateRequestID is called in side that query class. 
				[this.affinityService.AFFINITY_CONTEXT.REQUEST_ID]: this.affinityService.generateRequestID()
			};
			requestContext = Object.assign(requestContext, affinityContext);
			return requestContext;
		}

		_isQueryOptimizationEnabled() {
			var queryOptimizationProp = this.properties.getPropertyList().find((prop) => prop.id==='queryOptimization');
			return (queryOptimizationProp && queryOptimizationProp.getPropertyValue());
		}

		//Needed to serialize the featureModel into its spec.
		toJSON() {
			return this.featureModel;
		}

		/**
		 * !!!!! ATTENTION ATTENTION ATTENTION ATTENTION ATTENTION ATTENTION !!!!!
		 *
		 * The "dataCache" property is placed in the "advanced" property section which comes from dashboard-core
		 * (created in TranslationService.js). That means that this fiel is partially dependent on TranslationService
		 * and its dependencies.
		 */		
		getPropertyLayoutList() {
			const sectionId = this.featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled') ? 'advanced' : 'general.advanced';
			return [{
				id: InternalQueryService.PROPID_QUERY_OPTIMIZATION, //must correspond to id in getPropertyList
				editor: {
					sectionId: sectionId,
					position: 1,
					uiControl: {
						type: 'ToggleButton',
						label: StringResources.get('queryOptimization')
					}
				},
				supportsUndoRedo: true
			},
			{
				id: InternalQueryService.PROPID_DATA_CACHE, //must correspond to id in getPropertyList
				editor: {
					sectionId: sectionId,
					position: 2,
					uiControl: {
						type: 'DropDown',
						label: StringResources.get('dataCache'),
						options: [{
							label: StringResources.get('dataCache_auto'),
							value: 'automatic'
						}, {
							label: StringResources.get('dataCache_on'),
							value: 'yes'
						}, {
							label: StringResources.get('dataCache_off'),
							value: 'no'
						}]
					}
				}
			}];
		}

		getPropertyList() {
			const boardModel = this.internal && this.internal.getBoardModel();
			const propertiesModel = boardModel && boardModel.properties;
			return [{
				id: InternalQueryService.PROPID_DATA_CACHE,	//must correspond to id in getPropertyLayoutList
				setPropertyValue: (value) => {
					const modelledValue = value === 'automatic' ? undefined : value;
					propertiesModel && propertiesModel.set({
						'localCache': modelledValue
					});
				},
				getPropertyValue: () => { return propertiesModel && propertiesModel.get('localCache') || 'automatic'; }
			},
			{
				id: InternalQueryService.PROPID_QUERY_OPTIMIZATION,	//must correspond to id in getPropertyLayoutList
				setPropertyValue: (value) => {
					this.featureModel.queryOptimization = value;
				},
				getPropertyValue: () => {
					//Unless explicitly set false, its true (on)
					return !(this.featureModel.queryOptimization === false);
				},
				supportsUndoRedo: true
			}];
		}
	}

	return InternalQueryService;
});
